if (typeof SubscriptionUpgradeModal === 'undefined') {
  class SubscriptionUpgradeModal {
  constructor() {
    this.overlay = null;
    this.modal = null;
    this.body = null;
    this.closeButton = null;
    this.autoCloseTimeout = null;
    this.focusableElements = [];
    this.previousActiveElement = null;
    this.isInitialized = false;

    this.settings = {
      title: window.Shopify?.theme?.subscription_update_modal_title || 'Subscription Plan Update',
      bodyTemplate: window.Shopify?.theme?.subscription_update_modal_body || 'Your subscription plan for [product name] has been successfully changed from [current subscription label] to [new subscription label].',
      autoCloseDuration: window.Shopify?.theme?.subscription_update_modal_auto_close || 0
    };

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.createModal();
    this.cacheElements();
    this.attachEventListeners();
    this.isInitialized = true;
  }

  createModal() {
    const existingOverlay = document.getElementById('subscription-upgrade-modal-overlay');
    if (existingOverlay) {
      this.overlay = existingOverlay;
      this.modal = this.overlay.querySelector('.subscription-modal');
      this.closeButton = this.overlay.querySelector('.subscription-modal-close');
      this.body = this.overlay.querySelector('#subscription-modal-body');
      return;
    }

    this.overlay = document.createElement('div');
    this.overlay.id = 'subscription-upgrade-modal-overlay';
    this.overlay.className = 'subscription-modal-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-labelledby', 'subscription-modal-title');
    this.overlay.setAttribute('aria-describedby', 'subscription-modal-body');
    this.overlay.setAttribute('tabindex', '-1');

    this.modal = document.createElement('div');
    this.modal.className = 'subscription-modal';

    const header = document.createElement('div');
    header.className = 'subscription-modal-header';

    const title = document.createElement('h2');
    title.id = 'subscription-modal-title';
    title.className = 'subscription-modal-title';
    title.textContent = this.settings.title;

    this.closeButton = document.createElement('button');
    this.closeButton.type = 'button';
    this.closeButton.className = 'subscription-modal-close';
    this.closeButton.setAttribute('aria-label', 'Close modal');
    this.closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;

    header.appendChild(title);
    header.appendChild(this.closeButton);

    this.body = document.createElement('div');
    this.body.id = 'subscription-modal-body';
    this.body.className = 'subscription-modal-body';

    this.modal.appendChild(header);
    this.modal.appendChild(this.body);
    this.overlay.appendChild(this.modal);

    document.body.appendChild(this.overlay);
  }

  cacheElements() {
    this.body = this.overlay.querySelector('#subscription-modal-body');
    this.closeButton = this.overlay.querySelector('.subscription-modal-close');
  }

  attachEventListeners() {
    this.closeButton?.addEventListener('click', () => this.close());

    this.overlay?.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (!this.overlay?.classList.contains('is-active')) return;

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'Tab':
          this.handleTabKey(e);
          break;
      }
    });
  }

  static escapeHTML(str = '') {
    return str.replace(/[&<>'"]/g, (tag) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[tag]));
  }

  show(currentLabel, newLabel, productTitle) {
    if (!this.overlay || !this.body) return;

    this.previousActiveElement = document.activeElement;

    const safeProduct = SubscriptionUpgradeModal.escapeHTML(productTitle || 'Product');
    const safeCurrent = SubscriptionUpgradeModal.escapeHTML(currentLabel);
    const safeNew = SubscriptionUpgradeModal.escapeHTML(newLabel);

    const bodyText = this.settings.bodyTemplate
      .replace('[product name]', `<strong>${safeProduct}</strong>`)
      .replace('[current subscription label]', `<strong>${safeCurrent}</strong>`)
      .replace('[new subscription label]', `<strong>${safeNew}</strong>`);

    this.body.innerHTML = bodyText;

    requestAnimationFrame(() => {
      this.overlay.classList.add('is-active');
      this.overlay.focus();
      this.updateFocusableElements();
      this.announceToScreenReader(`${this.settings.title}. ${this.body.textContent}`);
    });

    if (this.settings.autoCloseDuration > 0) {
      this.autoCloseTimeout = setTimeout(() => this.close(), this.settings.autoCloseDuration * 1000);
    }
  }

  close() {
    if (!this.overlay) return;

    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }

    this.overlay.classList.remove('is-active');

    if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
      setTimeout(() => this.previousActiveElement.focus(), 300);
    }
  }

  updateFocusableElements() {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this.focusableElements = Array.from(this.modal.querySelectorAll(focusableSelectors));
  }

  handleTabKey(e) {
    if (this.focusableElements.length === 0) {
      e.preventDefault();
      return;
    }

    const first = this.focusableElements[0];
    const last = this.focusableElements[this.focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || document.activeElement === this.overlay) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  announceToScreenReader(message) {
    let liveRegion = document.getElementById('subscription-modal-live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'subscription-modal-live-region';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = message;
  }

  destroy() {
    this.overlay?.remove();
    document.getElementById('subscription-modal-live-region')?.remove();
    this.isInitialized = false;
  }
  }

  let subscriptionUpgradeModal = null;

  function initSubscriptionUpgradeModal() {
    if (!subscriptionUpgradeModal) {
      subscriptionUpgradeModal = new SubscriptionUpgradeModal();
    }
    return subscriptionUpgradeModal;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSubscriptionUpgradeModal);
  } else {
    initSubscriptionUpgradeModal();
  }

  document.addEventListener('shopify:section:load', initSubscriptionUpgradeModal);

  window.SubscriptionUpgradeModal = SubscriptionUpgradeModal;
  window.showSubscriptionUpgradeModal = (currentLabel, newLabel, productTitle) => {
    const isModalEnabled = window.Shopify?.theme?.enable_subscription_update_modal === true;
    if (!isModalEnabled) return;

    const modal = initSubscriptionUpgradeModal();
    modal.show(currentLabel, newLabel, productTitle);
  };
}